import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Divider, IconButton } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import PropTypes from "prop-types";
import { useMaterialUIController } from "context";

const SidebarItem = ({ item, isExpanded }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [controller] = useMaterialUIController();
  const { user } = controller;

  const [open, setOpen] = useState(() => {
    return JSON.parse(localStorage.getItem(`submenu_${item.name}`)) || false;
  });

  useEffect(() => {
    localStorage.setItem(`submenu_${item.name}`, JSON.stringify(open));
  }, [open, item.name]);

  const hasDropdown =
    item.type === "dropdown" || (item.collapse && item.collapse.length > 0 && !item.route);

  const handleClick = () => {
    if (!hasDropdown && item.route) {
      navigate(item.route);
    }
    if (hasDropdown && isExpanded) {
      setOpen((prev) => !prev);
    }
  };

  return (
    <Box
      sx={{
        p: 1.5,
        mb: 1,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        width: isExpanded ? "100%" : "60px",
        backgroundColor: location.pathname === item.route ? "#E59400" : "transparent",
        color: location.pathname === item.route ? "#fff" : "rgba(255, 255, 255, 0.7)",
        transition: "all 0.2s ease-in-out",
        borderRadius: "7px",
        "&:hover": {
          backgroundColor:
            location.pathname === item.route ? "transparent" : "rgba(255, 255, 255, 0.1)",
          color: "#ffffff",
        },
      }}
      onClick={handleClick}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
        {item.icon}
        {isExpanded && (
          <Typography
            sx={{
              ml: 2,
              flexGrow: 1,
              fontSize: "14px",
              fontWeight: "500",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.name}
          </Typography>
        )}
        {hasDropdown && isExpanded && (
          <IconButton size="small" sx={{ color: "#fff" }}>
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </Box>
      {isExpanded && hasDropdown && open && (
        <Box sx={{ mt: 1, pl: 1, width: "100%" }}>
          {item.collapse?.map((group, groupIndex) => (
            <Box key={groupIndex} sx={{ mb: 0 }}>
              {group.name && (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      color: "#CCFF99",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                  >
                    {group.name}
                  </Typography>
                  <Divider sx={{ mb: 1, bgcolor: "rgba(255, 255, 255, 0.1)" }} />
                </>
              )}
              {group.collapse?.map((subItem, subIndex) => {
                const isActive = location.pathname === subItem.route;
                return (
                  <Box
                    key={subIndex}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 0.5,
                      pl: 1,
                      mb: 0.5,
                      borderRadius: "7px",
                      cursor: "pointer",
                      backgroundColor: isActive ? "#E59400" : "transparent",
                      color: isActive ? "#FFFFFF" : "rgba(255, 255, 255, 0.7)",
                      "&:hover": {
                        backgroundColor: isActive ? "#E59400" : "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(subItem.route);
                    }}
                  >
                    {subItem.icon && <Box sx={{ mr: 1, mt: 1 }}>{subItem.icon}</Box>}
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "15px",
                        fontWeight: "400",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {subItem.name}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

SidebarItem.propTypes = {
  item: PropTypes.shape({
    type: PropTypes.string,
    name: PropTypes.string,
    key: PropTypes.string,
    route: PropTypes.string,
    icon: PropTypes.element,
    collapse: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        name: PropTypes.string,
        key: PropTypes.string,
        route: PropTypes.string,
        icon: PropTypes.element,
        collapse: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            key: PropTypes.string,
            route: PropTypes.string,
            icon: PropTypes.element,
          })
        ),
      })
    ),
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
};

export default SidebarItem;
